// Automatisations (Ordre 25) — words and small rules shared by Paramètres › Automatisations and its tests.
export const STATUS_LABELS = {
  done: "Réussie",
  skipped: "Ignorée",
  retry: "Nouvel essai prévu",
  failed: "En échec",
  running: "En cours",
};

// A skipped run always says why, in business words (the server keeps a short code).
export const REASON_LABELS = {
  module_off: "Le module concerné est éteint",
  object_gone: "L'objet n'existe plus",
  no_manager: "Aucun responsable de compte",
  manager_inactive: "Le responsable du compte est désactivé",
  already_done: "Déjà fait",
  no_account: "La pharmacie n'a pas de dossier client",
  account_closed: "Le dossier client est clos",
  no_pharmacy: "Aucune pharmacie rattachée",
  not_deferred: "La décision n'est plus différée",
  nobody_to_alert: "Personne à prévenir",
  ticket_closed: "Le ticket est déjà clos",
};

export const EFFECT_KIND_LABELS = { task: "Ouvre une tâche", history: "Écrit dans l'historique", alert: "Prévient des personnes" };

export function reasonLabel(code) {
  return REASON_LABELS[code] || (code ? "Situation non prévue" : "");
}

export function statusTone(status) {
  if (status === "done") return "ok";
  if (status === "failed") return "bad";
  if (status === "retry" || status === "running") return "warn";
  return "muted";
}

// What the run row says in one line: its effect when it worked, its reason when it was skipped, the error code when it failed.
export function runSummary(run) {
  if (run.status === "done") return run.effect || "Fait";
  if (run.status === "skipped") return reasonLabel(run.reason);
  if (run.status === "failed") return `Échec après ${run.attempts} essai${run.attempts > 1 ? "s" : ""}`;
  if (run.status === "retry") return `Essai ${run.attempts} échoué`;
  return "";
}

export function countsLine(counts) {
  const parts = [];
  if (counts.done) parts.push(`${counts.done} réussie${counts.done > 1 ? "s" : ""}`);
  if (counts.skipped) parts.push(`${counts.skipped} ignorée${counts.skipped > 1 ? "s" : ""}`);
  const bad = (counts.failed || 0) + (counts.retry || 0);
  if (bad) parts.push(`${bad} en échec`);
  return parts.length ? `7 derniers jours : ${parts.join(" · ")}` : "Aucune exécution ces 7 derniers jours";
}

// What stops a rule from running, in the order a person should look at it.
export function ruleState(rule, engineEnabled) {
  if (!rule.module_on) return { key: "module_off", label: "Module éteint", tone: "muted" };
  if (rule.paused) return { key: "paused", label: "En pause", tone: "bad" };
  if (!rule.enabled) return { key: "off", label: "Éteinte", tone: "muted" };
  if (!engineEnabled) return { key: "stopped", label: "Moteur arrêté", tone: "warn" };
  return { key: "on", label: "Active", tone: "ok" };
}

// The bounds come from the server (`parameters_meta`), so a new threshold needs no change here; the default keeps the two original ones.
const DEFAULT_META = [
  { name: "customer_followup_delay_days", min: 1, max: 60, unit: "jours" },
  { name: "max_runs_per_hour", min: 1, max: 1000, unit: "par heure" },
];

export function parametersErrors(values, meta = DEFAULT_META) {
  const errors = {};
  for (const { name, min, max, unit } of meta) {
    if (!(name in values)) continue;
    const n = Number(values[name]);
    if (values[name] === "" || !Number.isInteger(n) || n < min || n > max) {
      errors[name] = max === 1000 ? "Entre 1 et 1 000" : `Entre ${min} et ${max} ${unit && unit.startsWith("jours") ? "jours" : ""}`.trim();
    }
  }
  return errors;
}

// The catalogue is shown by module, in the order a person thinks about their work; a module not listed goes last.
export const MODULE_ORDER = ["prospects", "pilots", "customers", "missions", "tickets", "billing", "equipment", "training", "product", "governance", "procurement", "finance"];
export const MODULE_LABELS = {
  prospects: "Prospects", pilots: "Pilotes", customers: "Clients", missions: "Missions", tickets: "Support", billing: "Revenus",
  equipment: "Matériel", training: "Formations", product: "Produit", governance: "Gouvernance", procurement: "Achats", finance: "Finances internes",
};

export function groupRulesByModule(rules) {
  const rank = (m) => (MODULE_ORDER.includes(m) ? MODULE_ORDER.indexOf(m) : MODULE_ORDER.length);
  const groups = new Map();
  for (const rule of rules) {
    if (!groups.has(rule.module)) groups.set(rule.module, []);
    groups.get(rule.module).push(rule);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => rank(a) - rank(b))
    .map(([module, items]) => ({ module, label: MODULE_LABELS[module] || module, rules: items }));
}
